import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Image, UploadSimple, Trash, Info, Palette, ChatText } from "@phosphor-icons/react";

// Paid tenants upload a logo shown (with their org name) on their subdomain login screen.
export const BrandingCard = () => {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [color, setColor] = useState("#071b3e");
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/tenant/branding").then((r) => {
      setB(r.data);
      if (r.data?.brand_color) setColor(r.data.brand_color);
      setMsg(r.data?.login_message || "");
    }).catch(() => setB(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_500_000) { toast.error("Logo must be under 1.5 MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/tenant/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove your login logo?")) return;
    setBusy(true);
    try {
      await api.delete("/tenant/branding/logo");
      toast.success("Logo removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
    } finally { setBusy(false); }
  };

  const saveColor = async (value) => {
    try {
      await api.put("/tenant/branding/color", { color: value });
      toast.success(value ? "Accent color saved" : "Accent color reset");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save color");
    }
  };

  const saveMessage = async (value) => {
    try {
      await api.put("/tenant/branding/message", { message: value });
      toast.success(value ? "Login message saved" : "Login message cleared");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save message");
    }
  };

  if (!b) return null; // hidden for non-admins (403)

  const logoSrc = b.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}?t=${encodeURIComponent(b.logo_updated_at || "")}` : null;

  return (
    <Card data-testid="branding-card" className="p-6">
      <h3 className="text-lg font-semibold flex items-center gap-2 mb-1"><Image size={18} /> Login branding</h3>
      <p className="text-sm text-slate-500 mb-4">
        Upload your logo, set an accent color, and write a custom login message — they appear on your team's login screen.
      </p>

      {!b.can_brand ? (
        <div className="flex items-center gap-2 rounded-lg bg-slate-50 border px-4 py-3 text-sm text-slate-600">
          <Info size={16} /> Custom login branding is available on ClaroShield Light and ClaroShield plans. Free Trial entities use the standard ClaroShield branding.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4">
            <div className="h-20 w-40 flex items-center justify-center rounded-lg border bg-slate-900 overflow-hidden shrink-0">
              {logoSrc
                ? <img src={logoSrc} alt="Current logo" data-testid="branding-logo-preview" className="max-h-16 max-w-[140px] object-contain" />
                : <span className="text-xs text-white/50">No logo yet</span>}
            </div>
            <div className="flex-1">
              <div className="font-medium text-slate-800">{b.name}</div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
                     onChange={onPick} className="hidden" data-testid="branding-file-input" />
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" disabled={busy} data-testid="branding-upload-btn"
                        onClick={() => fileRef.current?.click()}>
                  <span className="flex items-center gap-1.5"><UploadSimple size={15} /> {busy ? "Uploading…" : b.has_logo ? "Replace logo" : "Upload logo"}</span>
                </Button>
                {b.has_logo && (
                  <Button size="sm" variant="ghost" disabled={busy} data-testid="branding-remove-btn"
                          onClick={removeLogo} className="text-red-600 hover:text-red-700">
                    <Trash size={15} />
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-2">PNG, JPG, SVG or WebP · up to 1.5 MB</p>
            </div>
          </div>
          <div className="mt-4 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-800 flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>Your branded login screen goes live at <b>{b.login_url}</b> once your dedicated subdomain is enabled.</span>
          </div>

          <div className="mt-5 border-t pt-4">
            <div className="font-medium text-slate-800 flex items-center gap-2"><Palette size={16} /> Accent color</div>
            {b.can_brand_color ? (
              <>
                <p className="text-sm text-slate-500 mt-1 mb-3">Match your login screen to your logo.</p>
                <div className="flex items-center gap-3">
                  <input type="color" data-testid="brand-color-input" value={color}
                         onChange={(e) => setColor(e.target.value)}
                         className="h-10 w-14 rounded border cursor-pointer bg-transparent p-0.5" />
                  <input type="text" value={color} onChange={(e) => setColor(e.target.value)}
                         data-testid="brand-color-hex"
                         className="h-10 w-32 rounded-md border border-input px-3 text-sm font-mono uppercase" />
                  <Button size="sm" data-testid="brand-color-save" onClick={() => saveColor(color)}
                          style={{ backgroundColor: color }} className="text-white">Save</Button>
                  {b.brand_color && (
                    <Button size="sm" variant="ghost" data-testid="brand-color-reset"
                            onClick={() => { setColor("#071b3e"); saveColor(""); }} className="text-slate-500">Reset</Button>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-slate-50 border px-4 py-3 text-sm text-slate-600">
                <Info size={16} /> Custom accent colors are available on ClaroShield Light and ClaroShield plans.
              </div>
            )}
          </div>

          <div className="mt-5 border-t pt-4">
            <div className="font-medium text-slate-800 flex items-center gap-2"><ChatText size={16} /> Login message</div>
            <p className="text-sm text-slate-500 mt-1 mb-2">Shown on your team's login screen in place of the default marketing copy — a great spot for a message to your employees.</p>
            <textarea data-testid="brand-message-input" value={msg} maxLength={400}
                      onChange={(e) => setMsg(e.target.value)} rows={3}
                      placeholder="e.g. Welcome, Acme Power team — log your Q3 evidence by Sept 30."
                      className="w-full rounded-md border border-input px-3 py-2 text-sm resize-y" />
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-slate-400">{msg.length}/400</span>
              <div className="flex-1" />
              {b.login_message && (
                <Button size="sm" variant="ghost" data-testid="brand-message-reset"
                        onClick={() => { setMsg(""); saveMessage(""); }} className="text-slate-500">Reset to default</Button>
              )}
              <Button size="sm" data-testid="brand-message-save" onClick={() => saveMessage(msg.trim())}>Save message</Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default BrandingCard;
